import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { shareReplay, map, tap } from 'rxjs/operators';

import { User } from './user';
import { UserModification } from './user-modification';
import { PinnedReport } from './pinned-report';
import { Account } from '../account/account';

import { ResponseWrapper } from '@library/common/http';

import { PrefetchService, Preferences, EnvironmentService } from '@library/core/services';

import { Auth, CognitoUser } from '@aws-amplify/auth';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

import * as FullStory from '@fullstory/browser';
import { IGenericType } from '@library/common/generictype';

@Injectable({
   providedIn: 'root',
})
export class UserService {
   url = 'user';

   working = false;

   _user: User;

   authSnackbar: MatSnackBarRef<any>;

   CognitoUser: CognitoUser;

   observable: Observable<any>;

   constructor(
      private http: HttpClient,
      private prefetch: PrefetchService,
      private prefs: Preferences,
      private snackbar: MatSnackBar,
      private e: EnvironmentService,
   ) { }

   get user(): Observable<User> {
      if ( this._user ) {
         return of( this._user );
      }

      if ( this.observable ) {
         return this.observable;
      }

      // newly logged in user, pull info from the db
      return this.observable = this.getUser()
         .pipe(
            tap( user => {
               const us = user;
               if (us) {
                  FullStory.setUserVars({
                     displayName: us.fname + ' ' + us.lname,
                     email: us.username,
                  });
               }
               const havePrimary = us.accounts.some( a => a?.primary == true );
               const haveActive = us.accounts.some( a => a?.active == true );
               if( havePrimary == false && haveActive == false ) {
                  user.preferences.start = [];
                  user.preferences.start.push( { label: "USER > ACCOUNTS", path: "/user/account" });
               }
               this._user = user;
               localStorage.removeItem( 'user' );
            }),
            tap( user => this.prefs.prefs = user.preferences ),
            tap( _ => this.prefs.listen = true ),
            tap( _ => this.prefetch.prefetch() ),
            tap( _ => this.observable = null ),
            shareReplay()
         );
   }

   get accounts(): Observable<Account[]> {
      return this.user.pipe( map( user => user.accounts ), shareReplay() );
   }

   get pinned_reports(): Observable<PinnedReport[]> {
      return this.getUser().pipe( map( user => user.pinned_reports ), shareReplay() );
   }

   update( modify: UserModification ): Observable<any> {
      if ( !modify ) { return of( null ); }

      return this.http.patch( this.url, modify, { params: { useLambda: true } } ).pipe( tap( _ => this.logout() ));
   }

   logout() {
      this.reset();
   }

   destroy(): void {
      this.reset();
   }

   reset(): void {
      this._user = null;
      localStorage.removeItem( 'user' );
      localStorage.removeItem('email');
      localStorage.removeItem('name');
   }

   getCognitoUser() {
      return Auth.currentUserPoolUser({ bypassCache: true }).then( CognitoUser => {
         return CognitoUser;
      }, error => {
         console.log (error);
      })
   }

   changePassword( currentUser: CognitoUser, oldPassword: string, newPassword: string ) {
      this.working = true;
      return Auth.changePassword( currentUser, oldPassword, newPassword ).then( _ => {
         this.authSnackbar = this.snackbar.open( 'Your password has been successfully changed!', 'Close', { duration: 15000 } );
         this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
         this.working = false;
         return true;
      }, error => {
         let message = '';

         if( error.message ) {
            if( error.message == 'Incorrect username or password.' ) {
               message = 'Incorrect Old Password';
            }
            message = error.message;
         } else {
            message = 'Unknown Password change error please contact your administrator';
         }

         this.authSnackbar = this.snackbar.open( message, 'Close', { duration: 15000 } );
         this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
         this.working = false;
         return false;
      })
   }

   private getUser() {
      let params: IGenericType = { useLambda: true };

      if (this.e.environment.portal === 'agent') {
         params = { useLambda: true };
      }

      return this.http.get<ResponseWrapper<User>>( this.url, { params } )
         .pipe(
            map( rw => rw.response.collection[0] ),
            map( user => user ?? throwError( () => new Error( `Missing User` ) ) ),
            map( user => new User( user )),
         );
   }
}
